import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Mail, ArrowLeft } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_release-nexus/artifacts/a0bdi5ug_Alignk%20backlink%20professional.png';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqs = [
    {
      category: "About Our Service",
      questions: [
        {
          question: "What is Alignk?",
          answer: "Alignk is a professional backlink building service. We help businesses improve their search engine rankings by securing high-quality, do-follow backlinks from authority websites with high Domain Authority (DA) scores."
        },
        {
          question: "What are backlinks and why do I need them?",
          answer: "Backlinks are links from other websites pointing to your website. They are one of Google's top ranking factors. When high-authority websites link to your site, search engines view your content as more trustworthy and valuable, which can help improve your search rankings."
        },
        {
          question: "Is Alignk affiliated with Google?",
          answer: "No. Alignk is an independent link building service company. We are not affiliated with, endorsed by, or partnered with Google, Bing, or any other search engine. We use white-hat SEO practices that comply with search engine guidelines."
        }
      ]
    },
    {
      category: "How It Works",
      questions: [
        {
          question: "How does your backlink service work?",
          answer: "1) Choose a backlink package that fits your needs, 2) Provide your website URL and target keywords, 3) Our team identifies the best link opportunities in our network, 4) We create quality content and secure editorial placements with backlinks to your site, 5) You receive a detailed report with all live backlink URLs and DA scores in your dashboard."
        },
        {
          question: "How long does it take to build backlinks?",
          answer: "Typical turnaround time is 5-7 business days for Starter packages, 7-10 days for Professional, and 10-14 days for Enterprise packages. We prioritize quality placements over speed to ensure the best results."
        },
        {
          question: "What information do I need to provide?",
          answer: "You'll need to provide: 1) Your website URL, 2) Target keywords you want to rank for, 3) Your industry/niche, 4) Any specific anchor text preferences. The more information you provide, the better we can customize your backlink strategy."
        }
      ]
    },
    {
      category: "Quality & Transparency",
      questions: [
        {
          question: "What types of websites will link to my site?",
          answer: "Our network includes news websites, industry blogs, business publications, and niche-specific platforms. All websites are vetted for quality, traffic, and domain authority. We provide detailed reports showing every backlink placement with live URLs and DA scores."
        },
        {
          question: "Are the backlinks permanent?",
          answer: "Yes, all backlinks we build are intended to be permanent. However, we cannot guarantee permanence as publication decisions are at the discretion of third-party websites. Based on our experience, the vast majority of backlinks remain live indefinitely."
        },
        {
          question: "Are the backlinks do-follow or no-follow?",
          answer: "All backlinks in our packages are do-follow links, which pass SEO value to your website. We do not provide nofollow links as part of our standard packages."
        },
        {
          question: "What Domain Authority (DA) websites do you use?",
          answer: "DA ranges vary by package: Starter (DA 20-40), Professional (DA 40-60), and Enterprise (DA 50-80+). We use Moz's Domain Authority metric and verify all websites before placement."
        }
      ]
    },
    {
      category: "Results & Expectations",
      questions: [
        {
          question: "Will backlinks guarantee higher rankings?",
          answer: "No. While backlinks are a major ranking factor, we cannot guarantee specific ranking improvements. Search rankings depend on many factors including your website quality, content, competition, technical SEO, and Google's algorithms. Backlinks are one piece of a comprehensive SEO strategy."
        },
        {
          question: "How soon will I see SEO results?",
          answer: "SEO results typically take 2-6 months to become visible. Search engines need time to discover, crawl, and evaluate new backlinks. Patience is important – quality backlinks build authority over time rather than producing instant results."
        },
        {
          question: "Do you use Private Blog Networks (PBNs)?",
          answer: "No. We never use PBNs, link farms, or spammy link building tactics. All our backlinks come from real, legitimate websites with genuine traffic. We only use white-hat, Google-compliant link building strategies."
        }
      ]
    },
    {
      category: "Pricing & Payment",
      questions: [
        {
          question: "What packages do you offer?",
          answer: "We offer three packages: Starter ($19.99 - 10 backlinks, DA 20-40), Professional ($49.99 - 23 backlinks, DA 40-60), and Enterprise ($199.99 - 35 backlinks, DA 50-80+). All packages include permanent do-follow links and detailed SEO reports."
        },
        {
          question: "Do you offer refunds?",
          answer: "Due to the nature of our service (work begins immediately after purchase), we offer refunds only in limited circumstances as outlined in our Refund Policy. Please review our refund policy before purchasing."
        },
        {
          question: "Are there any hidden fees?",
          answer: "No. All pricing is displayed upfront with no hidden fees. The price you see is the price you pay. There are no recurring charges unless you choose to purchase additional packages."
        }
      ]
    },
    {
      category: "Support & Contact",
      questions: [
        {
          question: "How can I track my order?",
          answer: "After purchase, you'll have access to your personal dashboard where you can track order status, view progress, and see all completed backlinks with live URLs and metrics."
        },
        {
          question: "What if I have questions after purchase?",
          answer: "Our support team is available via email at support@alignk.com. Enterprise clients also receive dedicated account manager support. We typically respond within 24-48 business hours."
        },
        {
          question: "Can I request specific websites for backlinks?",
          answer: "While we cannot guarantee placements on specific websites, Enterprise clients can discuss target preferences with their account manager. We'll do our best to accommodate requests within our network capabilities."
        }
      ]
    }
  ];

  let globalIndex = 0;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img src={LOGO_URL} alt="Alignk" className="h-10" />
            </Link>
            <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-cyan-600 transition">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-cyan-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Frequently Asked <span style={{ color: '#4AC5DB' }}>Questions</span>
          </h1>
          <p className="text-xl text-gray-600">
            Everything you need to know about our backlink building services
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {faqs.map((category, catIndex) => (
            <div key={catIndex} className="mb-12">
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#4AC5DB', fontFamily: 'Playfair Display' }}>
                {category.category}
              </h2>
              <div className="space-y-4">
                {category.questions.map((faq, qIndex) => {
                  const currentIndex = globalIndex++;
                  return (
                    <div key={qIndex} className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleFAQ(currentIndex)}
                        className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition"
                      >
                        <span className="font-medium text-gray-900">{faq.question}</span>
                        {openIndex === currentIndex ? (
                          <ChevronUp className="h-5 w-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-500" />
                        )}
                      </button>
                      {openIndex === currentIndex && (
                        <div className="px-6 pb-4 text-gray-600 leading-relaxed bg-gray-50">
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Still Have Questions?
          </h2>
          <p className="text-gray-600 mb-6">
            Can't find what you're looking for? Our support team is here to help.
          </p>
          <Link 
            to="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white font-medium transition hover:opacity-90"
            style={{ backgroundColor: '#4AC5DB' }}
          >
            <Mail className="h-5 w-5" />
            Contact Support
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} Alignk. All rights reserved.
          </p>
          <div className="mt-4 space-x-4 text-sm">
            <Link to="/privacy-policy" className="text-gray-400 hover:text-white">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-gray-400 hover:text-white">Terms of Use</Link>
            <Link to="/refund-policy" className="text-gray-400 hover:text-white">Refund Policy</Link>
            <Link to="/contact" className="text-gray-400 hover:text-white">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FAQ;
